import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Api from "../../../core/data/redux/utils/apiCall";
import { API_URLS } from "../../../core/data/redux/apis/urls";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  extractList,
  stripLeadingZeros,
  toApiDateTime,
  toDotDate,
  formatDotDate,
  mapAppraisalDetails,
  getAppraiseeIdFromStateOrStorage,
} from "./appraisalHelper";

const AppraisalDocumentList = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const user = useSelector(
    (state: any) => state.employeeAuthState?.user || state.authState?.user || state.auth?.user || state.user?.userData
  );

  // Filter state
  const [categoryGroupId, setCategoryGroupId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [appraisalTemplateId, setAppraisalTemplateId] = useState("");
  const [employee, setEmployee] = useState("");
  const [validityPeriodFrom, setValidityPeriodFrom] = useState("01.01.2019");
  const [validityPeriodTo, setValidityPeriodTo] = useState("31.12.9999");

  // Selection states
  const [selectedCatGroupId, setSelectedCatGroupId] = useState<string>("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  // Dropdown lists
  const [categoryGroupList, setCategoryGroupList] = useState<any[]>([]);
  const [templateList, setTemplateList] = useState<any[]>([]);
  const [employeeList, setEmployeeList] = useState<any[]>([]);

  // Data table state
  const [tableData, setTableData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [displayLoading, setDisplayLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  const getAppraiseeId = useCallback(() => {
    return getAppraiseeIdFromStateOrStorage(user);
  }, [user]);

  // Fetch Category Groups
  const fetchCategoryGroups = useCallback(async () => {
    try {
      const response: any = await Api.post(API_URLS.APPRAISAL.CATEGORY_GROUP, {});
      const list = extractList(response?.data).length > 0 ? extractList(response?.data) : extractList(response);
      setCategoryGroupList(list);
    } catch (err: any) {
      console.error("Error fetching category groups:", err);
    }
  }, []);

  // Fetch Appraisal Templates
  const fetchAppraisalTemplates = useCallback(async () => {
    try {
      const response: any = await Api.post(API_URLS.APPRAISAL.TEMPLATE, {});
      const list = extractList(response?.data).length > 0 ? extractList(response?.data) : extractList(response);
      setTemplateList(list);
    } catch (err: any) {
      console.error("Error fetching templates:", err);
    }
  }, []);

  // Fetch Appraisal Employees
  const fetchAppraisalEmployees = useCallback(async () => {
    try {
      const response: any = await Api.post(API_URLS.APPRAISAL.EMPLOYEES, { text: "" });
      const list = extractList(response?.data).length > 0 ? extractList(response?.data) : extractList(response);
      setEmployeeList(list);
    } catch (err: any) {
      console.error("Error fetching employees:", err);
    }
  }, []);

  useEffect(() => {
    fetchCategoryGroups();
    fetchAppraisalTemplates();
    fetchAppraisalEmployees();
  }, [fetchCategoryGroups, fetchAppraisalTemplates, fetchAppraisalEmployees]);

  const mapListItem = useCallback((item: any, index: number) => {
    const statusName = item?.StatusName || "";
    const isCompleted = String(statusName).toLowerCase() === "completed";
    return {
      id: String(item?.AppraisalID || index),
      appraisalId: item?.AppraisalID || "",
      templateId: item?.TemplateID || "",
      appraisalDocName: item?.AppraisalName || "-",
      appraisalStatus: isCompleted ? "Completed" : statusName || "In Process",
      substatus: item?.SubStatusName || item?.SubstatusName || "-",
      appraiseeName: item?.AppraiseeName || "-",
      appraiseeId: item?.AppraiseeID || item?.EmployeeNumber || item?.EmployeeID || "",
      periodFrom: toDotDate(item?.StartDate),
      periodTo: toDotDate(item?.EndDate),
      offlineInfo: "Download not permitted",
      documentType: item?.AppraisalDocumentType || "Appraisal Document",
      categoryGroupId: item?.CategoryGroupID || "",
      categoryId: item?.CategoryID || "",
      raw: item,
    };
  }, []);

  // Fetch document list
  const fetchDocumentList = useCallback(async () => {
    let appraiseeId = getAppraiseeId();
    if (!appraiseeId && employee) {
      const empData = employeeList.find((e: any) => e.Text === employee);
      if (empData) {
        appraiseeId = stripLeadingZeros(empData.Id || empData.EmpID || empData.EmployeeID || empData.Text);
      }
    }
    setIsLoading(true);
    const payload = {
      appraiseeId: appraiseeId || "",
      startDate: toApiDateTime(validityPeriodFrom, false),
      endDate: toApiDateTime(validityPeriodTo, true),
    };
    try {
      console.log("Calling APPRAISAL_DOCUMENT_LIST API with payload:", payload);
      const response: any = await Api.post(API_URLS.APPRAISAL.DOCUMENT_LIST, payload);
      console.log("APPRAISAL_DOCUMENT_LIST API response:", response);
      const res = response?.data;
      const extractedItems = extractList(res).length > 0 ? extractList(res) : extractList(response);
      const items = extractedItems.map(mapListItem);
      setTableData(items);
    } catch (err: any) {
      console.error("Error fetching document list:", err);
      toast.error(err?.message || "Error fetching document list");
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  }, [getAppraiseeId, employee, employeeList, mapListItem, validityPeriodFrom, validityPeriodTo]);

  useEffect(() => {
    fetchDocumentList();
  }, [fetchDocumentList]);

  // Frontend filter for appraisalTemplateId
  const filteredTableData = useMemo(() => {
    if (!appraisalTemplateId && !selectedTemplateId) {
      return tableData;
    }
    const targetId = (
      selectedTemplateId ||
      appraisalTemplateId.split(" - ")[0] ||
      appraisalTemplateId
    ).trim();

    return tableData.filter((item) => {
      const itemTemplateId = String(
        item?.templateId ??
        item?.raw?.TemplateID ??
        item?.raw?.ApprTemplateID ??
        item?.raw?.TemplateId ??
        ""
      ).trim();

      if (targetId && itemTemplateId) {
        if (
          itemTemplateId === targetId ||
          stripLeadingZeros(itemTemplateId) === stripLeadingZeros(targetId) ||
          itemTemplateId.toLowerCase() === targetId.toLowerCase()
        ) {
          return true;
        }
      }

      if (item?.appraisalDocName && appraisalTemplateId) {
        const desc = appraisalTemplateId.includes(" - ")
          ? appraisalTemplateId.split(" - ").slice(1).join(" - ").trim()
          : appraisalTemplateId;
        if (
          desc &&
          item.appraisalDocName.toLowerCase().includes(desc.toLowerCase())
        ) {
          return true;
        }
      }

      return false;
    });
  }, [tableData, appraisalTemplateId, selectedTemplateId]);

  // Summary stats
  const summaryStats = useMemo(
    () => ({
      totalAppraisals: filteredTableData.length,
      inProcessAppraisals: filteredTableData.filter(
        (r) => r.appraisalStatus !== "Completed"
      ).length,
      completedAppraisals: filteredTableData.filter(
        (r) => r.appraisalStatus === "Completed"
      ).length,
    }),
    [filteredTableData]
  );

  const openDocumentDetail = async (isEdit: boolean = false) => {
    if (selectedRequestId === null || displayLoading || editLoading) return;
    const selectedItem = filteredTableData.find((row) => row.id === selectedRequestId);
    if (!selectedItem) {
      toast.warn("Please select a document first.");
      return;
    }
    const appraisalId = selectedItem.appraisalId;
    if (!appraisalId) {
      toast.error("Invalid Appraisal ID");
      return;
    }
    const appraiseeId =
      stripLeadingZeros(
        selectedItem.appraiseeId ||
        selectedItem.raw?.AppraiseeID ||
        selectedItem.raw?.EmployeeNumber ||
        selectedItem.raw?.EmployeeID
      ) || getAppraiseeId();

    if (isEdit) {
      setEditLoading(true);
    } else {
      setDisplayLoading(true);
    }
    try {
      const response: any = await Api.post(API_URLS.APPRAISAL.DOCUMENT_DETAILS, {
        appraisalId,
        appraiseeId,
      });
      const res = response?.data;
      const detailsRaw =
        (Array.isArray(res?.data?.data?.data) ? res.data.data.data[0] : null) ||
        (Array.isArray(res?.data?.data) ? res.data.data[0] : null) ||
        (Array.isArray(res?.data) ? res.data[0] : null) ||
        (Array.isArray(res) ? res[0] : null) ||
        extractList(res)[0] ||
        extractList(response)[0] ||
        (res?.data?.data && typeof res.data.data === "object" ? res.data.data : null) ||
        (res?.data && typeof res.data === "object" ? res.data : null) ||
        res ||
        {};

      const appraisalDetail = mapAppraisalDetails(detailsRaw, selectedItem);
      navigate(`/apar/documents/details/${appraisalId}`, {
        state: {
          appraisalDetail,
          isEdit,
          mode: isEdit ? "edit" : "display",
        },
      });
    } catch (err: any) {
      toast.error(err?.message || "Error fetching details");
    } finally {
      if (isEdit) {
        setEditLoading(false);
      } else {
        setDisplayLoading(false);
      }
    }
  };

  const filteredTemplates = useMemo(() => {
    let list = templateList;
    if (selectedCategoryId) {
      list = list.filter(
        (item) => (item?.CategoryID || item?.CategoryId) === selectedCategoryId
      );
    } else if (selectedCatGroupId) {
      list = list.filter(
        (item) =>
          (item?.CategoryGroupID || item?.CategoryGroupId || item?.CatGroupId) ===
          selectedCatGroupId
      );
    }
    return list;
  }, [templateList, selectedCategoryId, selectedCatGroupId]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="page-title">Appraisal Documents</h3>
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={routes.apar.dashboard}>Employee Appraisal</Link>
                </li>
                <li className="breadcrumb-item active">Appraisal Documents</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="row mb-4">
          <div className="col-xl-4 col-md-4">
            <div className="card shadow-sm border-0 rounded-4 overflow-hidden mb-3">
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-13 fw-medium">Total Appraisals</span>
                  <h3 className="mb-0 mt-1 fw-bold text-dark">{summaryStats.totalAppraisals}</h3>
                </div>
                <div className="avatar avatar-md bg-primary-transparent rounded-3">
                  <i className="ti ti-file-description fs-20 text-primary"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-4 col-md-4">
            <div className="card shadow-sm border-0 rounded-4 overflow-hidden mb-3">
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-13 fw-medium">In Process</span>
                  <h3 className="mb-0 mt-1 fw-bold text-warning">
                    {summaryStats.inProcessAppraisals}
                  </h3>
                </div>
                <div className="avatar avatar-md bg-warning-transparent rounded-3">
                  <i className="ti ti-clock-play fs-20 text-warning"></i>
                </div>
              </div>
            </div>
          </div>

          <div className="col-xl-4 col-md-4">
            <div className="card shadow-sm border-0 rounded-4 overflow-hidden mb-3">
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div>
                  <span className="text-muted fs-13 fw-medium">Completed</span>
                  <h3 className="mb-0 mt-1 fw-bold text-success">
                    {summaryStats.completedAppraisals}
                  </h3>
                </div>
                <div className="avatar avatar-md bg-success-transparent rounded-3">
                  <i className="ti ti-circle-check fs-20 text-success"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Criteria */}
        <div className="card shadow-sm border-0 rounded-4 mb-4">
          <div className="card-header bg-transparent border-0 pt-3 pb-0 d-flex justify-content-between align-items-center">
            <h5 className="card-title fw-semibold mb-0">Selection Criteria</h5>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm px-3 rounded-2"
                onClick={() => {
                  setCategoryGroupId("");
                  setCategoryId("");
                  setAppraisalTemplateId("");
                  setEmployee("");
                  setSelectedCatGroupId("");
                  setSelectedCategoryId("");
                  setSelectedTemplateId("");
                  setValidityPeriodFrom("01.01.2019");
                  setValidityPeriodTo("31.12.9999");
                }}
              >
                <i className="ti ti-rotate-clockwise me-1"></i> Reset
              </button>
              <button
                className="btn btn-primary btn-sm px-4 rounded-2"
                onClick={fetchDocumentList}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="ti ti-loader fa-spin me-1"></i> Searching...
                  </>
                ) : (
                  <>
                    <i className="ti ti-search me-1"></i> Apply Filters
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="card-body p-3">
            <div className="row g-3">
              <div className="col-xl-3 col-md-6">
                <label className="form-label fs-13 text-muted mb-1">Category Group ID</label>
                <select
                  className="form-select form-select-sm"
                  value={categoryGroupId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCategoryGroupId(val);
                    const itemData = categoryGroupList.find(
                      (item) =>
                        `${item.CategoryId} - ${item.CategoryName}` === val ||
                        item.CategoryId === val
                    );
                    if (itemData) {
                      setSelectedCatGroupId(itemData.CatGroupId || "");
                      setSelectedCategoryId(itemData.CategoryId || "");
                      setCategoryId(
                        itemData.CategoryName
                          ? `${itemData.CategoryId} - ${itemData.CategoryName}`
                          : itemData.CategoryId
                      );
                    } else {
                      setSelectedCatGroupId("");
                      setSelectedCategoryId("");
                    }
                    setAppraisalTemplateId("");
                    setSelectedTemplateId("");
                  }}
                >
                  <option value="">Select Category Group</option>
                  {categoryGroupList.map((item, idx) => {
                    const formatted = item.CategoryName
                      ? `${item.CategoryId} - ${item.CategoryName}`
                      : item.CategoryId;
                    return (
                      <option key={idx} value={formatted}>
                        {formatted}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="col-xl-3 col-md-6">
                <label className="form-label fs-13 text-muted mb-1">Category ID</label>
                <select
                  className="form-select form-select-sm"
                  value={categoryId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCategoryId(val);
                    const itemData = categoryGroupList.find(
                      (item) =>
                        `${item.CategoryId} - ${item.CategoryName}` === val ||
                        item.CategoryId === val
                    );
                    if (itemData) {
                      setSelectedCatGroupId(itemData.CatGroupId || selectedCatGroupId || "");
                      setSelectedCategoryId(itemData.CategoryId || "");
                    }
                    setAppraisalTemplateId("");
                    setSelectedTemplateId("");
                  }}
                >
                  <option value="">Select Category</option>
                  {categoryGroupList.map((item, idx) => {
                    const formatted = item.CategoryName
                      ? `${item.CategoryId} - ${item.CategoryName}`
                      : item.CategoryId;
                    return (
                      <option key={idx} value={formatted}>
                        {formatted}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="col-xl-3 col-md-6">
                <label className="form-label fs-13 text-muted mb-1">Appraisal Template ID</label>
                <select
                  className="form-select form-select-sm"
                  value={appraisalTemplateId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAppraisalTemplateId(val);
                    const selectedOpt = filteredTemplates.find(
                      (item) =>
                        `${item.ApprTemplateID} - ${item.ApprTempIDDesc}` === val ||
                        item.ApprTemplateID === val
                    );
                    if (selectedOpt) {
                      setSelectedTemplateId(selectedOpt.ApprTemplateID);
                    } else {
                      setSelectedTemplateId(val.split(" - ")[0].trim());
                    }
                  }}
                >
                  <option value="">Select Appraisal Template</option>
                  {filteredTemplates.map((item, idx) => {
                    const formatted = item.ApprTempIDDesc
                      ? `${item.ApprTemplateID} - ${item.ApprTempIDDesc}`
                      : item.ApprTemplateID;
                    return (
                      <option key={idx} value={formatted}>
                        {formatted}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div className="col-xl-3 col-md-6">
                <label className="form-label fs-13 text-muted mb-1">Employee</label>
                <select
                  className="form-select form-select-sm"
                  value={employee}
                  onChange={(e) => setEmployee(e.target.value)}
                >
                  <option value="">Select Employee</option>
                  {employeeList.map((item, idx) => (
                    <option key={idx} value={item.Text || ""}>
                      {item.Text || ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-xl-3 col-md-6">
                <label className="form-label fs-13 text-muted mb-1">Validity Period From</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="DD.MM.YYYY"
                  value={validityPeriodFrom}
                  onChange={(e) => setValidityPeriodFrom(e.target.value)}
                />
              </div>

              <div className="col-xl-3 col-md-6">
                <label className="form-label fs-13 text-muted mb-1">Validity Period To</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="DD.MM.YYYY"
                  value={validityPeriodTo}
                  onChange={(e) => setValidityPeriodTo(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Header & Table */}
        <div className="card shadow-sm border-0 rounded-4">
          <div className="card-header bg-transparent border-0 pt-3 pb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h5 className="card-title fw-semibold mb-0">Appraisal Documents List</h5>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary btn-sm px-3 rounded-2"
                onClick={() => openDocumentDetail(false)}
                disabled={selectedRequestId === null || displayLoading}
              >
                {displayLoading ? (
                  <>
                    <i className="ti ti-loader fa-spin me-1"></i> Opening...
                  </>
                ) : (
                  <>
                    <i className="ti ti-eye me-1"></i> Display Details
                  </>
                )}
              </button>
              <button
                className="btn btn-primary btn-sm px-3 rounded-2"
                onClick={() => openDocumentDetail(true)}
                disabled={selectedRequestId === null || editLoading}
              >
                {editLoading ? (
                  <>
                    <i className="ti ti-loader fa-spin me-1"></i> Opening...
                  </>
                ) : (
                  <>
                    <i className="ti ti-edit me-1"></i> Edit Document
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="card-body p-0 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "50px" }} className="text-center">Select</th>
                    <th>Appraisal Document Name</th>
                    <th>Status</th>
                    <th>Substatus</th>
                    <th>Appraisee Name</th>
                    <th>Period From</th>
                    <th>Period To</th>
                    <th>Offline Info</th>
                    <th>Document Type</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4 text-muted">
                        <i className="ti ti-loader fa-spin fs-24 mb-2 d-block"></i>
                        Loading Appraisal Documents...
                      </td>
                    </tr>
                  ) : filteredTableData.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4 text-muted">
                        No appraisal documents found.
                      </td>
                    </tr>
                  ) : (
                    filteredTableData.map((item: any) => {
                      const isSelected = selectedRequestId === item.id;
                      const isCompleted = item.appraisalStatus === "Completed";
                      return (
                        <tr
                          key={item.id}
                          className={isSelected ? "table-active" : ""}
                          style={{ cursor: "pointer" }}
                          onClick={() =>
                            setSelectedRequestId((prev) => (prev === item.id ? null : item.id))
                          }
                        >
                          <td className="text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="radio"
                              className="form-check-input"
                              name="selectedAppraisal"
                              checked={isSelected}
                              onChange={() =>
                                setSelectedRequestId((prev) =>
                                  prev === item.id ? null : item.id
                                )
                              }
                            />
                          </td>
                          <td className="fw-semibold text-primary">{item.appraisalDocName}</td>
                          <td>
                            <span
                              className={`badge rounded-pill ${
                                isCompleted ? "bg-success-transparent text-success" : "bg-warning-transparent text-warning"
                              }`}
                            >
                              {item.appraisalStatus}
                            </span>
                          </td>
                          <td className="text-muted">{item.substatus}</td>
                          <td className="fw-medium text-dark">{item.appraiseeName}</td>
                          <td>{item.periodFrom}</td>
                          <td>{item.periodTo}</td>
                          <td>
                            <span className="text-muted fs-12">{item.offlineInfo}</span>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border">
                              {item.documentType}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppraisalDocumentList;
